import { isAxiosError } from 'axios'
import { apiClient } from '@/lib/api'
import type { AyahBookmark } from '@/lib/bookmarks'

export interface RemoteBookmarks {
  items: AyahBookmark[]
  updatedAt: string | null
}

/** 'unavailable' = backend not configured; null = network/auth error. */
export async function fetchRemoteBookmarks(): Promise<RemoteBookmarks | 'unavailable' | null> {
  try {
    const data = await apiClient.get<{ items?: unknown; updatedAt?: string | null }>('/bookmarks')
    return {
      items: Array.isArray(data.items) ? (data.items as AyahBookmark[]) : [],
      updatedAt: data.updatedAt ?? null,
    }
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 501) return 'unavailable'
    return null
  }
}

export async function pushRemoteBookmarks(items: AyahBookmark[], updatedAt: string): Promise<boolean> {
  try {
    await apiClient.put('/bookmarks', { items, updatedAt })
    return true
  } catch {
    return false
  }
}

/** Union two bookmark lists by verse key, keeping the most recently saved entry. */
export function mergeBookmarks(a: AyahBookmark[], b: AyahBookmark[]): AyahBookmark[] {
  const byKey = new Map<string, AyahBookmark>()
  for (const item of [...a, ...b]) {
    if (!item?.key) continue
    const existing = byKey.get(item.key)
    if (!existing || Date.parse(item.savedAt || '') > Date.parse(existing.savedAt || '')) {
      byKey.set(item.key, item)
    }
  }
  return [...byKey.values()].sort((x, y) => Date.parse(y.savedAt || '') - Date.parse(x.savedAt || ''))
}
